import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Headphones,
  Play,
  Pause,
  SkipForward,
  SkipBack,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  Repeat
} from 'lucide-react';
import { useContinuousPlayer } from '../context/ContinuousPlayerContext';
import SlokaArtImage from './SlokaArtImage';
import { formatTime } from '../utils/formatTime';

const AppleMiniPlayer = ({
  currentTrack,
  isPlaying,
  isLoading,
  togglePlay,
  playNext,
  playPrevious,
  playNextChapter,
  playPreviousChapter,
  canPlayNextChapter,
  canPlayPreviousChapter,
  totalTracks,
  currentIndex
}) => (
  <div className="mini-player">
    <div className="container mx-auto px-4 py-3 flex items-center gap-4">
      <SlokaArtImage
        chapterNumber={currentTrack.chapterNumber}
        slokaNumber={currentTrack.slokaNumber}
        alt=""
        className="w-12 h-12 rounded-lg object-cover object-top shrink-0 hidden sm:block"
      />
      <div className="min-w-0 flex-1">
        <p className="text-amber-100 text-sm font-medium truncate">{currentTrack.label}</p>
        <p className="text-amber-200/70 text-xs">
          Track {currentIndex + 1} of {totalTracks}
        </p>
      </div>
      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
        <button
          type="button"
          onClick={playPreviousChapter}
          className="player-btn"
          aria-label="Previous chapter"
          disabled={!canPlayPreviousChapter}
        >
          <ChevronsLeft className="w-5 h-5" />
        </button>
        <button type="button" onClick={playPrevious} className="player-btn" aria-label="Previous track">
          <SkipBack className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={togglePlay}
          disabled={isLoading}
          className="player-btn-primary"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5" />
          )}
        </button>
        <button type="button" onClick={playNext} className="player-btn" aria-label="Next track">
          <SkipForward className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={playNextChapter}
          className="player-btn"
          aria-label="Next chapter"
          disabled={!canPlayNextChapter}
        >
          <ChevronsRight className="w-5 h-5" />
        </button>
        <Link to="/listen" className="player-btn hidden sm:flex items-center gap-1 text-sm">
          <Headphones className="w-4 h-4" />
          Listen
        </Link>
      </div>
    </div>
    <div className="mini-player-progress">
      <div
        className="mini-player-progress-fill"
        style={{ width: `${totalTracks ? ((currentIndex + 1) / totalTracks) * 100 : 0}%` }}
      />
    </div>
  </div>
);

const StreamMiniPlayer = ({
  currentTrack,
  isPlaying,
  isLoading,
  togglePlay,
  playNext,
  playPrevious,
  playNextChapter,
  playPreviousChapter,
  canPlayNextChapter,
  canPlayPreviousChapter,
  toggleRepeat,
  settings,
  totalTracks,
  currentIndex,
  currentTime,
  duration,
  seek
}) => {
  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    seek(ratio * (duration || 0));
  };

  return (
    <footer className="spotify-player-bar spotify-player-bar-global">
      <div className="spotify-bar-progress" onClick={handleSeek}>
        <div className="spotify-bar-progress-fill" style={{ width: `${progressPct}%` }} />
      </div>
      <div className="spotify-bar-inner">
        <div className="spotify-bar-left">
          <SlokaArtImage
            chapterNumber={currentTrack.chapterNumber}
            slokaNumber={currentTrack.slokaNumber}
            alt=""
            className="spotify-bar-art"
          />
          <div className="min-w-0 hidden sm:block">
            <p className="text-sm font-medium truncate">{currentTrack.label}</p>
            <p className="text-xs text-white/50">Chapter {currentTrack.chapterNumber}</p>
          </div>
        </div>
        <div className="spotify-bar-center">
          <div className="spotify-bar-controls">
            <button
              type="button"
              onClick={toggleRepeat}
              className={`spotify-icon-btn ${settings.repeat === 'all' ? 'active' : ''}`}
              aria-label="Toggle loop"
            >
              <Repeat className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={playPreviousChapter}
              className="spotify-icon-btn"
              aria-label="Previous chapter"
              disabled={!canPlayPreviousChapter}
            >
              <ChevronsLeft className="w-5 h-5" />
            </button>
            <button type="button" onClick={playPrevious} className="spotify-icon-btn" aria-label="Previous track">
              <SkipBack className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={togglePlay}
              disabled={isLoading}
              className="spotify-bar-play"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5 ml-0.5" />
              )}
            </button>
            <button type="button" onClick={playNext} className="spotify-icon-btn" aria-label="Next track">
              <SkipForward className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={playNextChapter}
              className="spotify-icon-btn"
              aria-label="Next chapter"
              disabled={!canPlayNextChapter}
            >
              <ChevronsRight className="w-5 h-5" />
            </button>
          </div>
          <div className="spotify-bar-time hidden md:flex">
            <span>{formatTime(currentTime)}</span>
            <div className="spotify-bar-time-track" onClick={handleSeek}>
              <div className="spotify-bar-time-fill" style={{ width: `${progressPct}%` }} />
            </div>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
        <div className="spotify-bar-right hidden lg:flex items-center gap-3">
          <span className="text-xs text-white/50">
            {currentIndex + 1} / {totalTracks}
          </span>
          <Link to="/listen/stream" className="spotify-text-btn text-xs">
            Open Stream
          </Link>
        </div>
      </div>
    </footer>
  );
};

const MiniPlayer = () => {
  const location = useLocation();
  const {
    currentTrack,
    isPlaying,
    isLoading,
    togglePlay,
    playNext,
    playPrevious,
    playNextChapter,
    playPreviousChapter,
    canPlayNextChapter,
    canPlayPreviousChapter,
    toggleRepeat,
    settings,
    totalTracks,
    currentIndex,
    queue,
    playerVariant,
    currentTime,
    duration,
    seek
  } = useContinuousPlayer();

  if (queue.length === 0 || !currentTrack) {
    return null;
  }

  if (location.pathname.startsWith('/listen')) {
    return null;
  }

  if (playerVariant === 'stream') {
    return (
      <StreamMiniPlayer
        currentTrack={currentTrack}
        isPlaying={isPlaying}
        isLoading={isLoading}
        togglePlay={togglePlay}
        playNext={playNext}
        playPrevious={playPrevious}
        playNextChapter={playNextChapter}
        playPreviousChapter={playPreviousChapter}
        canPlayNextChapter={canPlayNextChapter}
        canPlayPreviousChapter={canPlayPreviousChapter}
        toggleRepeat={toggleRepeat}
        settings={settings}
        totalTracks={totalTracks}
        currentIndex={currentIndex}
        currentTime={currentTime}
        duration={duration}
        seek={seek}
      />
    );
  }

  return (
    <AppleMiniPlayer
      currentTrack={currentTrack}
      isPlaying={isPlaying}
      isLoading={isLoading}
      togglePlay={togglePlay}
      playNext={playNext}
      playPrevious={playPrevious}
      playNextChapter={playNextChapter}
      playPreviousChapter={playPreviousChapter}
      canPlayNextChapter={canPlayNextChapter}
      canPlayPreviousChapter={canPlayPreviousChapter}
      totalTracks={totalTracks}
      currentIndex={currentIndex}
    />
  );
};

export default MiniPlayer;
